import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Container,
  Table,
} from "reactstrap";
import MetaTags from "react-meta-tags";
import { useParams } from "react-router-dom";
import { AxiosResponse } from "axios";

import Breadcrumbs from "../../components/common/Breadcrumb";
import { ApiCore } from "../../helpers/api_helper";
import { Loading } from "../../components/common/Loading";
import { Endpoints } from "../../utils/Endpoints";
import StudentProfileCard from "./StudentProfileCard";
import { amountToCurrency } from "../../utils/Helpers";

interface StatementEntry {
  id: number;
  amount: number;
  date: string;
  particulars: string;
  category: string;
}

const request = new ApiCore();

const parseDate = (value: string): number => {
  // Expecting DD-MM-YYYY
  const parts = value?.split("-");
  if (parts?.length === 3) {
    const [dd, mm, yyyy] = parts;
    return new Date(Number(yyyy), Number(mm) - 1, Number(dd)).getTime();
  }
  return 0;
};

const StudentFinancial = () => {
  const { id } = useParams() as { id: string };

  const [loadingStudent, setLoadingStudent] = useState<boolean>(true);
  const [student, setStudent] = useState<any>(null);

  const [loadingStatement, setLoadingStatement] = useState<boolean>(true);
  const [statement, setStatement] = useState<StatementEntry[]>([]);

  useEffect(() => {
    request
      .get(`${Endpoints.students}/${id}`, {})
      .then((response: AxiosResponse) => {
        setStudent(response.data);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoadingStudent(false);
      });
  }, [id]);

  useEffect(() => {
    request
      .get(Endpoints.statement, { params: { student_id: id } })
      .then((response: AxiosResponse) => {
        setStatement(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoadingStatement(false);
      });
  }, [id]);

  const { rows, totals } = useMemo(() => {
    const sorted = [...statement].sort(
      (a, b) => parseDate(a.date) - parseDate(b.date)
    );

    let runningBalance = 0;
    let totalInvoiced = 0;
    let totalPaid = 0;

    const computed = sorted.map((entry) => {
      const isInvoice = entry.category === "invoice";
      const debit = isInvoice ? entry.amount : 0;
      const credit = !isInvoice ? entry.amount : 0;

      runningBalance += debit - credit;
      totalInvoiced += debit;
      totalPaid += credit;

      return {
        ...entry,
        debit,
        credit,
        balance: runningBalance,
      };
    });

    return {
      rows: computed,
      totals: {
        invoiced: totalInvoiced,
        paid: totalPaid,
        balance: runningBalance,
      },
    };
  }, [statement]);

  const isEmpty = !loadingStatement && rows.length === 0;

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Student Financial</title>
        </MetaTags>

        <Container fluid>
          <Breadcrumbs title="Students" breadcrumbItem="Financial" />

          <Row>
            <Col md={12}>
              <StudentProfileCard loading={loadingStudent} student={student} />
            </Col>
          </Row>

          {!loadingStatement && !isEmpty && (
            <Row>
              <Col md={4}>
                <Card className="border-start border-3 border-primary">
                  <CardBody>
                    <div className="text-muted small">Total Invoiced</div>
                    <h4 className="mb-0">{amountToCurrency(totals.invoiced)}</h4>
                  </CardBody>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-start border-3 border-success">
                  <CardBody>
                    <div className="text-muted small">Total Paid</div>
                    <h4 className="mb-0">{amountToCurrency(totals.paid)}</h4>
                  </CardBody>
                </Card>
              </Col>
              <Col md={4}>
                <Card
                  className={`border-start border-3 ${
                    totals.balance > 0 ? "border-danger" : "border-secondary"
                  }`}
                >
                  <CardBody>
                    <div className="text-muted small">Balance</div>
                    <h4
                      className={`mb-0 ${
                        totals.balance > 0 ? "text-danger" : ""
                      }`}
                    >
                      {amountToCurrency(totals.balance)}
                    </h4>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          <Row>
            <Col md={12}>
              <Card>
                <CardHeader>
                  <CardTitle className="h5 mb-0">Financial Statement</CardTitle>
                </CardHeader>
                <CardBody className="p-0">
                  {loadingStatement && <Loading label="Loading statement..." />}
                  {isEmpty && (
                    <p className="text-muted mb-0 p-3">No statement available.</p>
                  )}
                  {!loadingStatement && !isEmpty && (
                    <Table responsive className="mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Date</th>
                          <th>Particulars</th>
                          <th>Category</th>
                          <th className="text-end">Debit</th>
                          <th className="text-end">Credit</th>
                          <th className="text-end">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((entry) => (
                          <tr key={entry.id}>
                            <td className="text-nowrap">{entry.date}</td>
                            <td>{entry.particulars}</td>
                            <td>
                              <span
                                className={`badge ${
                                  entry.category === "invoice"
                                    ? "bg-soft-primary text-primary"
                                    : "bg-soft-success text-success"
                                }`}
                              >
                                {entry.category}
                              </span>
                            </td>
                            <td className="text-end">
                              {entry.debit ? amountToCurrency(entry.debit) : "—"}
                            </td>
                            <td className="text-end">
                              {entry.credit ? amountToCurrency(entry.credit) : "—"}
                            </td>
                            <td
                              className={`text-end ${
                                entry.balance > 0 ? "text-danger" : ""
                              }`}
                            >
                              {amountToCurrency(entry.balance)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="fw-semibold">
                          <td colSpan={3} className="text-end">
                            Totals
                          </td>
                          <td className="text-end">{amountToCurrency(totals.invoiced)}</td>
                          <td className="text-end">{amountToCurrency(totals.paid)}</td>
                          <td
                            className={`text-end ${
                              totals.balance > 0 ? "text-danger" : ""
                            }`}
                          >
                            {amountToCurrency(totals.balance)}
                          </td>
                        </tr>
                      </tfoot>
                    </Table>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default StudentFinancial;
